import React, { useState, useEffect } from 'react';
import { Calendar, ArrowRight, Package } from 'lucide-react';
import { Dialog } from '../../../components/Dialog';
import { productionApi } from '../../../services/api';

interface TransferProductionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  unit: string;
  sourceDate: string; // YYYY-MM-DD
  availableQuantity: number;
  onSuccess: () => void;
}

export const TransferProductionDialog: React.FC<TransferProductionDialogProps> = ({
  isOpen,
  onClose,
  productId,
  productName,
  unit,
  sourceDate,
  availableQuantity,
  onSuccess,
}) => {
  const [quantity, setQuantity] = useState<number>(0);
  const [destinationDate, setDestinationDate] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setQuantity(0);
      setDestinationDate('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validations
    if (quantity <= 0) {
      setError('La cantidad debe ser mayor a 0');
      return;
    }

    if (quantity > availableQuantity) {
      setError(`No puedes transferir más de ${availableQuantity} ${unit}`);
      return;
    }

    if (!destinationDate) {
      setError('Debes seleccionar una fecha de destino');
      return;
    }

    if (destinationDate === sourceDate) {
      setError('La fecha de destino debe ser diferente a la de origen');
      return;
    }

    try {
      setIsSubmitting(true);

      await productionApi.transferProduction({
        productId,
        unit,
        quantity,
        sourceDate,
        destinationDate,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error transferring production:', err);
      setError(err.message || 'Error al traspasar la producción');
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Traspasar Producción"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Product Info */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="text-green-600" size={20} />
            <h4 className="font-semibold text-slate-900">{productName}</h4>
          </div>
          <p className="text-sm text-slate-600">
            Disponible: <span className="font-bold text-green-700">{availableQuantity} {unit}</span>
          </p>
          <p className="text-sm text-slate-600">
            Fecha origen: <span className="font-medium">{new Date(sourceDate).toLocaleDateString('es-ES')}</span>
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Quantity Input */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-slate-700 mb-1">
            Cantidad a traspasar <span className="text-red-500">*</span>
          </label>
          <input
            id="quantity"
            type="number"
            min="0.01"
            step="0.01"
            max={availableQuantity}
            value={quantity || ''}
            onChange={(e) => setQuantity(Number(e.target.value))}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder={`Máximo: ${availableQuantity}`}
            disabled={isSubmitting}
            required
          />
          <p className="mt-1 text-xs text-slate-500">
            Unidad: {unit}
          </p>
        </div>

        {/* Destination Date */}
        <div>
          <label htmlFor="destinationDate" className="block text-sm font-medium text-slate-700 mb-1">
            Fecha de destino <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              id="destinationDate"
              type="date"
              value={destinationDate}
              onChange={(e) => setDestinationDate(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              disabled={isSubmitting}
              required
            />
          </div>
        </div>

        {/* Visual Transfer Indicator */}
        {quantity > 0 && destinationDate && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <div className="text-center">
                <p className="text-slate-600">Origen</p>
                <p className="font-bold text-slate-900">{new Date(sourceDate).toLocaleDateString('es-ES')}</p>
                <p className="text-red-600 font-medium">-{quantity} {unit}</p>
              </div>
              <ArrowRight className="text-blue-600" size={24} />
              <div className="text-center">
                <p className="text-slate-600">Destino</p>
                <p className="font-bold text-slate-900">{new Date(destinationDate).toLocaleDateString('es-ES')}</p>
                <p className="text-green-600 font-medium">+{quantity} {unit}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            )}
            Traspasar Producción
          </button>
        </div>
      </form>
    </Dialog>
  );
};
